import _ from 'lodash'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDebouncedCallback } from 'use-debounce'
import { SubmitHandler, useForm, UseFormReturn } from 'react-hook-form'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import LoadingButton from '@mui/lab/LoadingButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'

import TextInputController from '@/components/ui/inputs/TextInputController'
import AutocompleteInputController from '@/components/ui/inputs/AutocompleteInputController'

import { useLazyGetStaffsQuery } from '@/core/services/staff'
import { useLazyGetSpecialityOptionsQuery } from '@/core/services/speciality-option'

export type WaitingRoomsSearchFormValues = {
  doctors: string[]
  name: string
  specialities: string[]
}

export type WaitingRoomsSearchFormProps = {
  direction?: 'column' | 'row'
  onReset?: () => void
  onSubmit: SubmitHandler<WaitingRoomsSearchFormValues>
}

export function WaitingRoomsSearchForm(props: WaitingRoomsSearchFormProps) {
  const { direction = 'column', onReset, onSubmit } = props
  const { t } = useTranslation()

  const rhForm = useForm<WaitingRoomsSearchFormValues>({ defaultValues: { doctors: [], name: '', specialities: [] } })

  return (
    <Box sx={{ padding: '0 15px' }}>
      <Typography variant="h2" sx={{ mt: '30px', mb: '30px' }}>
        {t('page.consultations.consultationsSearchForm.title')}
      </Typography>
      <form onSubmit={rhForm.handleSubmit(onSubmit, console.log)}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={direction === 'column' ? 12 : 4}>
            <TextInputController control={rhForm.control} fullWidth label={t('field.name.label')} name="name" />
          </Grid>
          <Grid item xs={12} sm={direction === 'column' ? 12 : 4}>
            <DoctorsField rhForm={rhForm} />
          </Grid>
          <Grid item xs={12} sm={direction === 'column' ? 12 : 4}>
            <SpecialitiesField rhForm={rhForm} />
          </Grid>
          <Grid item xs={12}>
            <Stack direction="row" justifyContent="end" spacing={1}>
              <Button
                onClick={() => {
                  rhForm.reset()
                  onReset?.()
                }}
                variant="neutral"
              >
                {t('common:button.reset.label')}
              </Button>
              <LoadingButton type="submit" variant="contained">
                {t('common:button.submit.label')}
              </LoadingButton>
            </Stack>
          </Grid>
        </Grid>
      </form>
    </Box>
  )
}

function DoctorsField({ rhForm }: { rhForm: UseFormReturn<WaitingRoomsSearchFormValues> }) {
  const { t } = useTranslation()

  const [getStaffs, { data }] = useLazyGetStaffsQuery()

  const staffs = useMemo(() => data?.items ?? [], [data])

  const doctorOptions = useMemo(
    () => _.map(_.filter(staffs, ['status', 'MEDECIN']), (doctor) => ({ label: `${doctor.userInfo.name} ${doctor.userInfo.surname}`, value: doctor._id })),
    [staffs]
  )

  const handleInputChange = useDebouncedCallback((value: string) => {
    const [name, surname] = value.split(' ')
    getStaffs({ query: { name, surname } })
  }, 300)

  return (
    <AutocompleteInputController
      control={rhForm.control}
      label={t('field.doctor.label', { context: 'plural' })}
      multiple
      name="doctors"
      onInputChange={handleInputChange}
      options={doctorOptions}
    />
  )
}

function SpecialitiesField({ rhForm }: { rhForm: UseFormReturn<WaitingRoomsSearchFormValues> }) {
  const { t } = useTranslation()

  const [getSpecialityOptions, { data }] = useLazyGetSpecialityOptionsQuery()

  const specialityOptions = useMemo(() => data?.map(({ name }) => ({ label: name, value: name })) ?? [], [data])

  const handleInputChange = useDebouncedCallback((value: string) => {
    getSpecialityOptions({ smartSearch: value })
  }, 300)

  return (
    <AutocompleteInputController
      control={rhForm.control}
      label={t('field.speciality.label', { context: 'plural' })}
      multiple
      name="specialities"
      onInputChange={handleInputChange}
      options={specialityOptions}
    />
  )
}

export default WaitingRoomsSearchForm
